import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class CreditNoteService {

    private _baseUrl: string = "";
    private _controllerName: string = "";

    constructor(private _http: HttpClient) {
        this._baseUrl = environment.apiUrl;
        this._controllerName = "CreditNotes"
    }

    /**
     * Crea una nueva nota crédito
     * @param payload Datos de la nota crédito
     * @returns Observable con el resultado
     */
    create(payload: any): Observable<any> {
        return this._http.post<any>(`${this._baseUrl}/${this._controllerName}`, payload)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Obtiene todas las notas crédito
     * @returns Observable con la lista de notas crédito
     */
    getAll(): Observable<any> {
        return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/`)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Obtiene una nota crédito por ID
     * @param id ID de la nota crédito
     * @returns Observable con la nota crédito
     */
    getById(id: number): Observable<any> {
        return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/${id}`)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Obtiene notas crédito por banco
     * @param bankId ID del banco
     * @returns Observable con la lista de notas crédito del banco
     */
    getByBankId(bankId: number): Observable<any> {
        return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/bank/${bankId}`)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Obtiene notas crédito por participante
     * @param participantId ID del participante
     * @returns Observable con la lista de notas crédito del participante
     */
    getByParticipantId(participantId: number): Observable<any> {
        return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/participant/${participantId}`)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Anula una nota crédito
     * @param id ID de la nota crédito
     * @param cancellationReason Motivo de la anulación
     * @returns Observable con el resultado
     */
    cancel(id: number, cancellationReason: string): Observable<any> {
        const payload = { cancellationReason };
        return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/${id}/cancel`, payload)
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Obtiene notas crédito con paginación, búsqueda y filtros del lado servidor
     */
    getPagedAsync(parameters: any): Observable<any> {
        let params = new HttpParams();
        
        // Paginación
        if (parameters.pageNumber !== undefined && parameters.pageNumber !== null) {
            params = params.append('pageNumber', parameters.pageNumber.toString());
        }
        if (parameters.pageSize !== undefined && parameters.pageSize !== null) {
            params = params.append('pageSize', parameters.pageSize.toString());
        }
        
        // Búsqueda
        if (parameters.searchTerm) {
            params = params.append('searchTerm', parameters.searchTerm);
        }
        
        // Ordenamiento
        if (parameters.sortBy) {
            params = params.append('sortBy', parameters.sortBy);
        }
        if (parameters.sortOrder) {
            params = params.append('sortOrder', parameters.sortOrder);
        }
        
        // Filtros de fecha
        if (parameters.startDate) {
            params = params.append('startDate', parameters.startDate.toISOString());
        }
        if (parameters.endDate) {
            params = params.append('endDate', parameters.endDate.toISOString());
        }
        if (parameters.dateType) {
            params = params.append('dateType', parameters.dateType);
        }
        
        // Filtros específicos
        if (parameters.bankId !== undefined && parameters.bankId !== null) {
            params = params.append('bankId', parameters.bankId.toString());
        }
        if (parameters.cancellationFilter) {
            params = params.append('cancellationFilter', parameters.cancellationFilter);
        }
        if (parameters.projectId !== undefined && parameters.projectId !== null) {
            params = params.append('projectId', parameters.projectId.toString());
        }
        if (parameters.participantId !== undefined && parameters.participantId !== null) {
            params = params.append('participantId', parameters.participantId.toString());
        }
        if (parameters.thirdPartyId !== undefined && parameters.thirdPartyId !== null) {
            params = params.append('thirdPartyId', parameters.thirdPartyId.toString());
        }

        return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/paged`, { params })
            .pipe(catchError(this.handleErrors));
    }

    /**
     * Descarga el PDF de la nota crédito
     * @param id ID de la nota crédito
     * @returns Observable con el archivo Blob
     */
    downloadPdf(id: number): Observable<Blob> {
        return this._http.get(`${this._baseUrl}/${this._controllerName}/${id}/pdf`, { responseType: 'blob' })
            .pipe(catchError(this.handleErrors));
    }

    private handleErrors(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.log("Ocurrió un error", error.error.message);
        } else {
            console.log(`${error.status}, ${error.error}`);
            return throwError(() => new Error(`Ocurrió un error ${error.status}, ${error.error}`));
        }
        return throwError(() => new Error('Algo salió mal; por favor, intente nuevamente más tarde.'));
    }
}
